/**
 * Client-side job scraper for careers pages.
 * Fetches HTML via CORS proxy, parses common job listing patterns.
 *
 * Exports: scrapeJobsFromUrl, validateUrl, sanitizeHtml, parseJobListings, getRateLimitStatus
 */

const CORS_PROXY = process.env.REACT_APP_CORS_PROXY_URL || 'https://corsproxy.io/?';
const RATE_LIMIT_MS = 30000; // 30 seconds between scrapes
const FETCH_TIMEOUT_MS = 30000; // 30 second fetch timeout
const MAX_RESPONSE_BYTES = 5 * 1024 * 1024; // 5MB response size limit

let lastScrapeTimestamp = 0;

// Private/reserved IP ranges to block (SSRF prevention)
const BLOCKED_HOSTS = [
  /^localhost$/i,
  /^0\.0\.0\.0$/,
  /^::1$/,
  /^127\./,
  /^10\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^192\.168\./,
  /^0\./,
  /^\[::1\]/,
  /^169\.254\./,
  /^fc00:/i,
  /^fe80:/i,
];

// Schemes that must be blocked (SSRF prevention)
// eslint-disable-next-line no-script-url
const BLOCKED_SCHEMES = ['javascript:', 'data:', 'file:', 'ftp:', 'blob:', 'about:'];

/**
 * Validate a URL for scraping safety (SSRF prevention).
 * @param {string} url
 * @returns {{ valid: boolean, error?: { code: string, message: string } }}
 */
export function validateUrl(url) {
  if (!url || typeof url !== 'string') {
    return { valid: false, error: { code: 'INVALID_URL', message: 'Please enter a valid URL starting with https://' } };
  }

  const trimmed = url.trim();

  // Check blocked schemes before parsing
  for (const scheme of BLOCKED_SCHEMES) {
    if (trimmed.toLowerCase().startsWith(scheme)) {
      return { valid: false, error: { code: 'BLOCKED_URL', message: 'This URL cannot be accessed for security reasons' } };
    }
  }

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { valid: false, error: { code: 'INVALID_URL', message: 'Please enter a valid URL starting with https://' } };
  }

  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return { valid: false, error: { code: 'BLOCKED_URL', message: 'This URL cannot be accessed for security reasons' } };
  }

  const hostname = parsed.hostname.toLowerCase();
  for (const pattern of BLOCKED_HOSTS) {
    if (pattern.test(hostname)) {
      return { valid: false, error: { code: 'BLOCKED_URL', message: 'This URL cannot be accessed for security reasons' } };
    }
  }

  if (!hostname || hostname.length === 0) {
    return { valid: false, error: { code: 'INVALID_URL', message: 'Please enter a valid URL starting with https://' } };
  }

  return { valid: true };
}

/**
 * Strip HTML tags, scripts, and event handlers from text.
 * Returns plain text safe for storage and display.
 * @param {string} text
 * @returns {string}
 */
export function sanitizeHtml(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    // Remove dangerous elements and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    // Remove event handlers
    .replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    // Decode HTML entities BEFORE stripping tags (prevents encoded XSS payloads)
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Strip ALL tags (catches both original tags and decoded entity tags)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Check rate limit status for scraping.
 * @returns {{ canScrape: boolean, cooldownSeconds: number }}
 */
export function getRateLimitStatus() {
  const elapsed = Date.now() - lastScrapeTimestamp;
  if (elapsed >= RATE_LIMIT_MS) {
    return { canScrape: true, cooldownSeconds: 0 };
  }
  return { canScrape: false, cooldownSeconds: Math.ceil((RATE_LIMIT_MS - elapsed) / 1000) };
}

/**
 * Fetch and parse job listings from a careers page URL.
 * Returns { jobs, error? } — never throws.
 * @param {string} url - The careers page URL
 * @returns {Promise<{ jobs: Array, error?: { code: string, message: string } }>}
 */
export async function scrapeJobsFromUrl(url) {
  // Rate limit check
  const rateStatus = getRateLimitStatus();
  if (!rateStatus.canScrape) {
    return { jobs: [], error: { code: 'RATE_LIMITED', message: 'Please wait before scanning again' } };
  }

  // URL validation
  const validation = validateUrl(url);
  if (!validation.valid) {
    return { jobs: [], error: validation.error };
  }

  // Record scrape timestamp for rate limiting
  lastScrapeTimestamp = Date.now();

  // Lever has a public JSON API — use it directly (no CORS proxy needed)
  const leverCompany = extractLeverCompany(url);
  if (leverCompany) {
    return fetchLeverJobs(leverCompany);
  }

  // Fetch HTML via CORS proxy
  let html;
  try {
    const proxyUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl, {
      headers: { 'Accept': 'text/html,application/xhtml+xml' },
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });

    if (!response.ok) {
      return {
        jobs: [],
        error: { code: 'SCRAPE_FAILED', message: 'Could not access this careers page. Check the URL and try again.' },
      };
    }

    // Check Content-Length before reading body to prevent memory exhaustion
    const contentLength = parseInt(response.headers.get('content-length') || '0', 10);
    if (contentLength > MAX_RESPONSE_BYTES) {
      return {
        jobs: [],
        error: { code: 'SCRAPE_FAILED', message: 'The page is too large to process.' },
      };
    }

    html = await response.text();

    // Also check actual size (Content-Length can be missing or inaccurate)
    if (html.length > MAX_RESPONSE_BYTES) {
      return {
        jobs: [],
        error: { code: 'SCRAPE_FAILED', message: 'The page is too large to process.' },
      };
    }
  } catch (err) {
    const isTimeout = err.name === 'TimeoutError' || err.name === 'AbortError';
    const isNetwork = err.name === 'TypeError' || err.message?.includes('fetch');
    return {
      jobs: [],
      error: {
        code: isTimeout ? 'SCRAPE_FAILED' : (isNetwork ? 'NETWORK_ERROR' : 'SCRAPE_FAILED'),
        message: isTimeout
          ? 'The request timed out. The page may be too slow to respond.'
          : isNetwork
            ? 'Network error. Check your connection and try again.'
            : 'Could not access this careers page. Check the URL and try again.',
      },
    };
  }

  // Parse job listings
  let jobs;
  try {
    jobs = parseJobListings(html, url);
  } catch {
    return {
      jobs: [],
      error: { code: 'PARSE_ERROR', message: 'No job listings found on this page. Try a different URL.' },
    };
  }

  if (!jobs || jobs.length === 0) {
    return {
      jobs: [],
      error: { code: 'PARSE_ERROR', message: 'No job listings found on this page. Try a different URL.' },
    };
  }

  return { jobs };
}

/**
 * Parse job listings from HTML content.
 * Uses multiple heuristics to find jobs in common careers page formats.
 * @param {string} html - Raw HTML content
 * @param {string} baseUrl - Base URL for resolving relative links
 * @returns {Array<{ title: string, department?: string, location?: string, description?: string, url?: string }>}
 */
export function parseJobListings(html, baseUrl) {
  if (!html || typeof html !== 'string') return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const seen = new Set();

  // Extract JSON-LD structured data BEFORE removing script tags
  const jsonLdJobs = extractJsonLdJobs(doc, seen);

  // Remove dangerous elements
  doc.querySelectorAll('script, style, noscript, iframe, object, embed, form').forEach(el => el.remove());

  const jobs = [...jsonLdJobs];

  // If JSON-LD found jobs, prefer those over HTML heuristics
  if (jobs.length > 0) return jobs;

  // Strategy 1: Look for structured job listing patterns (common ATS systems)
  // Greenhouse, Lever, Workday, etc. use specific patterns
  const selectors = [
    // Greenhouse
    '.opening a[href*="/jobs/"], .opening a[href*="/positions/"]',
    // Lever
    '.posting a.posting-title',
    // Generic job listing patterns
    '[class*="job-listing"] a, [class*="job-card"] a, [class*="job-item"] a',
    '[class*="position-listing"] a, [class*="career-listing"] a',
    '[data-job] a, [data-posting] a',
    // Links containing job-related keywords in href
    'a[href*="/job/"], a[href*="/jobs/"], a[href*="/career"], a[href*="/position"]',
    'a[href*="/opening"], a[href*="/apply"]',
  ];

  for (const selector of selectors) {
    try {
      const elements = doc.querySelectorAll(selector);
      for (const el of elements) {
        const job = extractJobFromElement(el, baseUrl);
        if (job && !seen.has(job.title.toLowerCase())) {
          seen.add(job.title.toLowerCase());
          jobs.push(job);
        }
      }
    } catch {
      // Selector may be invalid, skip
    }
  }

  // Strategy 2: If no structured results, look for heading + link patterns
  if (jobs.length === 0) {
    const headings = doc.querySelectorAll('h2 a, h3 a, h4 a');
    for (const heading of headings) {
      const href = heading.getAttribute('href');
      if (href && isJobLink(href)) {
        const job = extractJobFromElement(heading, baseUrl);
        if (job && !seen.has(job.title.toLowerCase())) {
          seen.add(job.title.toLowerCase());
          jobs.push(job);
        }
      }
    }
  }

  // Strategy 3: Look for list items with links that appear to be job postings
  if (jobs.length === 0) {
    const listItems = doc.querySelectorAll('li a, tr a');
    for (const link of listItems) {
      const href = link.getAttribute('href');
      const text = link.textContent?.trim();
      if (href && text && text.length > 5 && text.length < 200 && isJobLink(href)) {
        const job = extractJobFromElement(link, baseUrl);
        if (job && !seen.has(job.title.toLowerCase())) {
          seen.add(job.title.toLowerCase());
          jobs.push(job);
        }
      }
    }
  }

  return jobs;
}

/**
 * Extract jobs from JSON-LD JobPosting structured data.
 * Must be called BEFORE removing script tags from the document.
 */
function extractJsonLdJobs(doc, seen) {
  const jobs = [];
  const scripts = doc.querySelectorAll('script[type="application/ld+json"]');
  for (const script of scripts) {
    try {
      const data = JSON.parse(script.textContent);
      const items = Array.isArray(data) ? data : [data];
      for (const item of items) {
        if (item['@type'] !== 'JobPosting' || !item.title) continue;
        const title = sanitizeHtml(item.title);
        if (!title || seen.has(title.toLowerCase())) continue;

        const loc = item.jobLocation;
        const locationStr = typeof loc === 'string'
          ? loc
          : loc?.address?.addressLocality || loc?.name || '';

        jobs.push({
          title,
          department: sanitizeHtml(item.department || item.occupationalCategory || '') || null,
          location: sanitizeHtml(locationStr) || null,
          description: sanitizeHtml(item.description || '').substring(0, 500) || null,
          url: item.url || null,
        });
        seen.add(title.toLowerCase());
      }
    } catch {
      // Invalid JSON-LD, skip
    }
  }
  return jobs;
}

/**
 * Extract job info from a DOM element and its surrounding context.
 */
function extractJobFromElement(el, baseUrl) {
  const link = el.tagName === 'A' ? el : el.querySelector('a');
  const title = cleanText(el.textContent);

  if (!title || title.length < 3 || title.length > 200) return null;
  // Filter out non-job links
  if (/^(home|about|contact|blog|sign|log|privacy|terms|back|next|prev)/i.test(title)) return null;

  const href = link?.getAttribute('href');
  let url = null;
  if (href) {
    try {
      url = new URL(href, baseUrl).toString();
    } catch {
      // Invalid URL, skip
    }
  }

  // Look for department/location in sibling or parent elements
  const container = el.closest('[class*="job"], [class*="posting"], [class*="position"], li, tr, article, div') || el.parentElement;
  const department = extractMetadata(container, ['department', 'team', 'group', 'category']);
  const location = extractMetadata(container, ['location', 'office', 'city', 'region']);
  const description = extractDescription(container);

  return {
    title,
    department: department || null,
    location: location || null,
    description: description || null,
    url,
  };
}

/**
 * Look for metadata text in an element's children.
 */
function extractMetadata(container, keywords) {
  if (!container) return null;

  // Check for elements with matching class/data attributes
  for (const keyword of keywords) {
    const el = container.querySelector(
      `[class*="${keyword}"], [data-${keyword}], .${keyword}`
    );
    if (el) {
      const text = cleanText(el.textContent);
      if (text && text.length < 100) return text;
    }
  }

  // Check for labeled spans: "Location: San Diego"
  const allText = container.querySelectorAll('span, small, p, div');
  for (const el of allText) {
    const text = el.textContent?.trim() || '';
    for (const keyword of keywords) {
      const pattern = new RegExp(`${keyword}\\s*[:–—]\\s*(.+)`, 'i');
      const match = text.match(pattern);
      if (match && match[1].length < 100) return match[1].trim();
    }
  }

  return null;
}

/**
 * Extract a brief description from a container element.
 */
function extractDescription(container) {
  if (!container) return null;
  const descEl = container.querySelector(
    '[class*="description"], [class*="summary"], [class*="snippet"], p'
  );
  if (descEl) {
    const text = cleanText(descEl.textContent);
    if (text && text.length > 20) return text.substring(0, 500);
  }
  return null;
}

/**
 * Check if a URL looks like a job posting link.
 */
function isJobLink(href) {
  return /\/(job|jobs|position|positions|career|careers|opening|openings|apply|posting)/i.test(href);
}

/**
 * Clean text: collapse whitespace, trim.
 */
function cleanText(text) {
  if (!text) return '';
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Extract Lever company slug from a jobs.lever.co URL.
 * Matches: https://jobs.lever.co/nextech, https://jobs.lever.co/nextech/
 * @returns {string|null} Company slug or null if not a Lever URL
 */
function extractLeverCompany(url) {
  try {
    const parsed = new URL(url.trim());
    if (parsed.hostname === 'jobs.lever.co') {
      const slug = parsed.pathname.split('/').filter(Boolean)[0];
      return slug || null;
    }
  } catch {
    // Not a valid URL
  }
  return null;
}

/**
 * Fetch jobs from Lever's public API (CORS-friendly, no proxy needed).
 * @param {string} company - Lever company slug (e.g. "nextech")
 * @returns {Promise<{ jobs: Array, error?: { code: string, message: string } }>}
 */
async function fetchLeverJobs(company) {
  try {
    const response = await fetch(
      `https://api.lever.co/v0/postings/${encodeURIComponent(company)}?mode=json`,
      { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) }
    );

    if (!response.ok) {
      return {
        jobs: [],
        error: { code: 'SCRAPE_FAILED', message: 'Could not access this careers page. Check the URL and try again.' },
      };
    }

    const postings = await response.json();
    if (!Array.isArray(postings) || postings.length === 0) {
      return {
        jobs: [],
        error: { code: 'PARSE_ERROR', message: 'No job listings found for this company.' },
      };
    }

    const jobs = postings.map(p => ({
      title: sanitizeHtml(p.text || ''),
      department: sanitizeHtml(p.categories?.department || p.categories?.team || '') || null,
      location: sanitizeHtml(
        [p.categories?.location, p.categories?.commitment].filter(Boolean).join(' — ')
      ) || null,
      description: sanitizeHtml(p.descriptionPlain || '').substring(0, 500) || null,
      url: p.hostedUrl || null,
    })).filter(j => j.title);

    return { jobs };
  } catch (err) {
    const isTimeout = err.name === 'TimeoutError' || err.name === 'AbortError';
    return {
      jobs: [],
      error: {
        code: 'SCRAPE_FAILED',
        message: isTimeout
          ? 'The request timed out. Try again.'
          : 'Could not access Lever jobs. Check the URL and try again.',
      },
    };
  }
}
